import {
  DestroyRef,
  effect,
  type ElementRef,
  inject,
  Injectable,
  type Signal,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';

/**
 * Reusable service for attaching/detaching elements to/from the document body.
 * Eliminates duplicated portal logic across modals, dropdowns, and previews.
 *
 * Usage in a component constructor:
 * ```typescript
 * private readonly portal = inject(PortalService);
 * readonly portalRef = viewChild<ElementRef<HTMLElement>>('myPortal');
 *
 * constructor() {
 *   this.portal.attach(this.portalRef, this.isOpen, inject(DestroyRef));
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class PortalService {
  private readonly doc = inject(DOCUMENT);

  /**
   * Attaches an element to document.body when the signal is truthy,
   * removes it when falsy. Cleans up on destroy to prevent DOM leaks.
   *
   * @param destroyRef Required — ensures cleanup when component is destroyed.
   */
  attach(
    elementRef: Signal<ElementRef<HTMLElement> | undefined>,
    isOpen: Signal<unknown>,
    destroyRef: DestroyRef
  ): void {
    effect(() => {
      const wrapper = elementRef()?.nativeElement;
      if (!wrapper) return;

      if (isOpen()) {
        this.doc.body.appendChild(wrapper);
      } else if (wrapper.parentElement === this.doc.body) {
        wrapper.remove();
      }
    });

    destroyRef.onDestroy(() => {
      const wrapper = elementRef()?.nativeElement;
      if (wrapper?.parentElement === this.doc.body) {
        wrapper.remove();
      }
    });
  }
}
